"use client";

import type { ReactNode } from "react";
import { BUTTON_SMALL, TABLE, TD, TH } from "@/components/tool-kit/styles";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type CodePointDetails,
  displayGlyph,
  formatBytes,
  formatUnits,
} from "@/lib/tool-kit/text/unicode-inspect";

/** One grapheme cluster and the code points it is made of. */
export interface ClusterDetails {
  number: number;
  text: string;
  index: number;
  codePoints: CodePointDetails[];
}

/** Every word the table shows, from the inspector widget's strings. */
export interface CodePointTableStrings {
  caption: string;
  clusterRow: string;
  clusterCodePoints: string;
  colChar: string;
  colCodePoint: string;
  colName: string;
  colCategory: string;
  colScript: string;
  colBlock: string;
  colUtf8: string;
  colDetails: string;
  showDetails: string;
  hideDetails: string;
  detailsFor: string;
  detailUtf16: string;
  detailHtml: string;
  detailHtmlDecimal: string;
  detailHtmlHex: string;
  detailJs: string;
  detailJsPair: string;
  detailCss: string;
  detailUrl: string;
  detailAge: string;
  detailControl: string;
  detailAbbreviation: string;
  detailCorrection: string;
  detailAlternate: string;
  none: string;
  unassignedAge: string;
}

const COLUMN_COUNT = 8;
const SYMBOLIC =
  "rounded border border-border bg-muted px-1 font-mono text-[11px] text-muted-foreground";

function DetailItem({ term, value }: { term: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-xs">{term}</dt>
      <dd className="select-all break-all font-mono text-foreground text-xs">
        {value}
      </dd>
    </div>
  );
}

function Details({
  details,
  strings,
}: {
  details: CodePointDetails;
  strings: CodePointTableStrings;
}) {
  const { aliases } = details;
  const entities = details.entities.map((name) => `&${name};`).join(" ");
  const age =
    details.age === "unassigned" ? strings.unassignedAge : details.age;
  return (
    <dl className="grid gap-3 sm:grid-cols-3">
      <DetailItem
        term={strings.detailUtf16}
        value={formatUnits(details.utf16)}
      />
      <DetailItem term={strings.detailHtml} value={entities || strings.none} />
      <DetailItem
        term={strings.detailHtmlDecimal}
        value={details.htmlDecimal}
      />
      <DetailItem term={strings.detailHtmlHex} value={details.htmlHex} />
      <DetailItem term={strings.detailJs} value={details.javascript} />
      {details.javascriptPair ? (
        <DetailItem
          term={strings.detailJsPair}
          value={details.javascriptPair}
        />
      ) : null}
      <DetailItem term={strings.detailCss} value={details.css} />
      <DetailItem term={strings.detailUrl} value={details.url} />
      {age ? <DetailItem term={strings.detailAge} value={age} /> : null}
      {aliases.control ? (
        <DetailItem term={strings.detailControl} value={aliases.control} />
      ) : null}
      {aliases.abbreviation ? (
        <DetailItem
          term={strings.detailAbbreviation}
          value={aliases.abbreviation}
        />
      ) : null}
      {aliases.correction ? (
        <DetailItem
          term={strings.detailCorrection}
          value={aliases.correction}
        />
      ) : null}
      {aliases.alternate ? (
        <DetailItem term={strings.detailAlternate} value={aliases.alternate} />
      ) : null}
    </dl>
  );
}

function CodePointRow({
  details,
  open,
  onToggle,
  strings,
}: {
  details: CodePointDetails;
  open: boolean;
  onToggle: () => void;
  strings: CodePointTableStrings;
}) {
  const glyph = displayGlyph(details.codePoint, details.aliases);
  const category = details.category
    ? `${details.category} · ${details.categoryName}`
    : "";
  return (
    <>
      <tr>
        <td className={`${TD} text-center text-lg`}>
          {glyph.symbolic ? (
            <span className={SYMBOLIC}>{glyph.text}</span>
          ) : (
            glyph.text
          )}
        </td>
        <td className={`${TD} whitespace-nowrap font-mono text-xs`}>
          {details.hex}
        </td>
        <td className={`${TD} font-mono text-xs`}>{details.name}</td>
        <td className={`${TD} text-xs`}>{category}</td>
        <td className={`${TD} text-xs`}>{details.script}</td>
        <td className={`${TD} text-xs`}>{details.block}</td>
        <td className={`${TD} whitespace-nowrap font-mono text-xs`}>
          {formatBytes(details.utf8) || strings.none}
        </td>
        <td className={TD}>
          <button
            aria-expanded={open}
            aria-label={t(strings.detailsFor, { codePoint: details.hex })}
            className={BUTTON_SMALL}
            onClick={onToggle}
            type="button"
          >
            {open ? strings.hideDetails : strings.showDetails}
          </button>
        </td>
      </tr>
      {open ? (
        <tr>
          <td className={`${TD} bg-muted/30`} colSpan={COLUMN_COUNT}>
            <Details details={details} strings={strings} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** A cluster as its heading shows it: a lone space or control by its abbreviation. */
function clusterText(cluster: ClusterDetails): string {
  const [first] = cluster.codePoints;
  if (cluster.codePoints.length === 1 && first) {
    return displayGlyph(first.codePoint, first.aliases).text;
  }
  return cluster.text;
}

/**
 * The inspector's table: a heading row for each grapheme cluster, then one row per
 * code point in it, each with a details row that opens on demand.
 */
export function CodePointTable({
  clusters,
  isOpen,
  onToggle,
  strings,
}: {
  clusters: readonly ClusterDetails[];
  isOpen: (key: string) => boolean;
  onToggle: (key: string) => void;
  strings: CodePointTableStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
          {strings.caption}
        </caption>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.colChar}
            </th>
            <th className={TH} scope="col">
              {strings.colCodePoint}
            </th>
            <th className={TH} scope="col">
              {strings.colName}
            </th>
            <th className={TH} scope="col">
              {strings.colCategory}
            </th>
            <th className={TH} scope="col">
              {strings.colScript}
            </th>
            <th className={TH} scope="col">
              {strings.colBlock}
            </th>
            <th className={TH} scope="col">
              {strings.colUtf8}
            </th>
            <th className={TH} scope="col">
              {strings.colDetails}
            </th>
          </tr>
        </thead>
        {clusters.map((cluster) => (
          <tbody key={cluster.index}>
            <tr className="bg-muted/40">
              <th
                className="px-3 py-1.5 text-left font-medium text-foreground text-xs"
                colSpan={COLUMN_COUNT}
                scope="rowgroup"
              >
                {t(strings.clusterRow, {
                  number: fmt(cluster.number),
                  text: clusterText(cluster),
                })}
                {" · "}
                {tPlural(
                  strings.clusterCodePoints,
                  locale,
                  cluster.codePoints.length,
                  { count: fmt(cluster.codePoints.length) }
                )}
              </th>
            </tr>
            {cluster.codePoints.map((details, position) => {
              const key = `${cluster.index}:${position}`;
              return (
                <CodePointRow
                  details={details}
                  key={key}
                  onToggle={() => onToggle(key)}
                  open={isOpen(key)}
                  strings={strings}
                />
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}
